import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils.ts';
import { ToneMark } from './tone-mark.tsx';

export type NoticeTone = 'clarify' | 'check' | 'ready' | 'info';

/** Status-Fläche und Status-Schrift je Ton (DESIGN „Status“: vier Paare Schrift auf zarter Fläche). */
export const NOTICE_TONES: Readonly<Record<NoticeTone, { surface: string; ink: string }>> = {
  clarify: { surface: 'bg-clarify-bg', ink: 'text-clarify' },
  check: { surface: 'bg-check-bg', ink: 'text-check' },
  ready: { surface: 'bg-ready-bg', ink: 'text-ready' },
  info: { surface: 'bg-info-bg', ink: 'text-info' },
};

/**
 * Hinweis-Karte (DESIGN „Seitenaufbau“ › Hinweis-Karte, Ticket 1.271; [RUDOLF 24.10.2026]): offene Prüfung oder Hinweis
 * auf der Status-Fläche (`clarify-bg`, `check-bg`, `ready-bg`, `info-bg`), Radius `tile`, Innenabstand 16. Formmarke
 * (nie Farbe allein), Titel 15/500 und Fußzeile `text-caption` in der Status-Schrift, Satz `text-body` `ink-2`,
 * Hauptknopf rechts daneben. Blendet in 200 ms ein (bei „Bewegung reduzieren“ sofort).
 * `aside` (Story 22.9): Signatur-Element rechts vor dem Hauptknopf, z. B. ein Countdown-Ring „noch 28 Tage“.
 */
export function NoticeCard({
  tone,
  title,
  children,
  footer,
  action,
  mark: markProp,
  aside,
  className,
  testId,
}: {
  tone: NoticeTone;
  title: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** Hauptknopf rechts (`Button`), höchstens einer. */
  action?: ReactNode;
  /** Eigenes Zeichen statt der Formmarke (z. B. Haken beim Freude-Moment, Ticket 1.272, dekorativ; Vorschaubild des Treffers, Ticket 1.302). */
  mark?: ReactNode;
  /** Zähler oder Ring rechts neben dem Text (vor dem Hauptknopf). */
  aside?: ReactNode;
  className?: string | undefined;
  testId?: string | undefined;
}) {
  const look = NOTICE_TONES[tone];
  const mark = (
    <span className={cn('flex flex-none items-center pt-space-1', look.ink)}>
      {markProp ?? (tone === 'info' ? <Info aria-hidden data-mark="info" size={14} strokeWidth={2} /> : <ToneMark tone={tone} />)}
    </span>
  );
  const body = (
    <div className="flex min-w-0 flex-1 flex-col gap-space-1">
      <p className={cn('m-0 text-lead font-medium', look.ink)}>{title}</p>
      {children ? <div className="text-body text-ink-2">{children}</div> : null}
      {footer ? <p className={cn('m-0 text-caption', look.ink)}>{footer}</p> : null}
    </div>
  );
  return (
    <div data-notice-card="" data-tone={tone} data-testid={testId} className={cn('flex min-w-0 animate-fade-in flex-wrap items-start gap-space-3 rounded-tile p-space-4', look.surface, className)}>
      {aside ? (
        <>
          {/* Mit Zähler (`aside`): am Handy Text über die ganze Breite, Zähler und Hauptknopf darunter; ab `sm` eine Zeile. */}
          <div className="flex min-w-0 flex-1 basis-full items-start gap-space-3 sm:basis-0">
            {mark}
            {body}
          </div>
          <div className="flex flex-none items-center gap-space-3 self-center">
            {aside}
            {action}
          </div>
        </>
      ) : (
        <>
          {mark}
          {body}
          {/* Am Handy steht der Hauptknopf in eigener Zeile, sonst drückt er den Satz auf wenige Zeichen zusammen. */}
          {action ? <div className="flex flex-none items-center self-center max-tablet:basis-full max-tablet:pl-space-6">{action}</div> : null}
        </>
      )}
    </div>
  );
}
